import { useEffect, useMemo } from 'react';

import { usePointChangeDevice } from '@entities/point';
import { useUserBillingInfo } from '@entities/billing';
import { useAccountCompanyCountry, useAccountCompanyId, useAccountCompanyIsBillingEnabled } from 'application/hooks';

import { useUserFeaturesOnboardingPass, useUserFeaturesOnboarding } from '@entities/media';
import { Country, PointPureChangeStreamMutationInput, UserFeatures } from '@shared/api/graphql/generated';
import { usePointChangeStream, useStreamCopyTemplate, useStreamsSaved } from '@entities/stream';

import { TPlayerTypeItem, UseSelectPlayerModalViewModel } from './types';
import { PLAYER_TEXT_TYPE } from './consts';
import { PlayerTypesInFlow } from '../types';

interface UseSelectPlayerModalViewModelProps {
  pointHasVideoSupport?: boolean;
}

const useSelectPlayerModalViewModel = ({
  pointHasVideoSupport,
}: UseSelectPlayerModalViewModelProps): UseSelectPlayerModalViewModel => {
  const { request: pointChangeStreamRequest, loading: isChangeStreamLoading } = usePointChangeStream();
  const streamCopyTemplate = useStreamCopyTemplate();
  const savedStreams = useStreamsSaved();

  const changeStream = async (inputs: PointPureChangeStreamMutationInput[]): Promise<boolean> => {
    const result = await pointChangeStreamRequest(inputs);
    return result;
  };

  const copyTemplateStream = async (templateId: string, companyId: string, withThematic: boolean = false) => {
    const result = await streamCopyTemplate.request(templateId, companyId, withThematic);
    return result;
  };

  const getSavedStreams = async (companyIds: string[]) => {
    const result = await savedStreams.request({ companyIds });
    return result;
  };

  const isCopyStreamTemplateLoading = streamCopyTemplate.loading;
  const isSavedStreamsLoading = savedStreams.loading;

  const { getBillingInfo } = useUserBillingInfo();
  const { request: pointSetDevice } = usePointChangeDevice();

  const companyId = useAccountCompanyId();
  const companyCountry = useAccountCompanyCountry();
  const isBillingEnabled = useAccountCompanyIsBillingEnabled();

  const { request: onboardingPassedRequest, result } = useUserFeaturesOnboardingPass();
  const { refetch: refetchFeaturesOnboarding } = useUserFeaturesOnboarding({
    features: [UserFeatures.WarningStreamFirstStart],
  });

  useEffect(() => {
    if (isBillingEnabled) {
      getBillingInfo(companyId);
    }
  }, []);

  const playersData = useMemo(() => {
    const players = [] as TPlayerTypeItem[];

    for (const type in PlayerTypesInFlow) {
      if (Object.prototype.hasOwnProperty.call(PlayerTypesInFlow, type)) {
        const playerType = type as PlayerTypesInFlow;

        const hideDevice =
          PLAYER_TEXT_TYPE[playerType].ruByOnly && !(companyCountry === Country.Ru || companyCountry === Country.By);

        const isHideVideoDevice = playerType === PlayerTypesInFlow.VIDEOPLAYER && !pointHasVideoSupport;

        if (!hideDevice && !isHideVideoDevice) {
          players.push({
            title: PLAYER_TEXT_TYPE[playerType].title,
            description: PLAYER_TEXT_TYPE[playerType].description,
            descriptionFallback: PLAYER_TEXT_TYPE[playerType].descriptionFallback,
            type: playerType,
            imageSrc: PLAYER_TEXT_TYPE[playerType].imageSrc,
            imageSrcFallback: PLAYER_TEXT_TYPE[playerType].imageSrcFallback,
          });
        }
      }
    }

    return players;
  }, []);

  const videoPlayersData = [
    {
      title: PLAYER_TEXT_TYPE[PlayerTypesInFlow.MOBILE].title,
      description: PLAYER_TEXT_TYPE[PlayerTypesInFlow.MOBILE].description,
      descriptionFallback: PLAYER_TEXT_TYPE[PlayerTypesInFlow.MOBILE].descriptionFallback,
      type: PlayerTypesInFlow.MOBILE,
      imageSrc: PLAYER_TEXT_TYPE[PlayerTypesInFlow.MOBILE].imageSrc,
    },
    {
      title: PLAYER_TEXT_TYPE[PlayerTypesInFlow.VIDEOPLAYER].title,
      description: PLAYER_TEXT_TYPE[PlayerTypesInFlow.VIDEOPLAYER].description,
      type: PlayerTypesInFlow.VIDEOPLAYER,
      imageSrc: PLAYER_TEXT_TYPE[PlayerTypesInFlow.VIDEOPLAYER].imageSrc,
    },
  ];

  return {
    playersData,
    videoPlayersData,
    isChangeStreamLoading,
    isCopyStreamTemplateLoading,
    isSavedStreamsLoading,
    isUserFeaturesOnboardingPassLoading: result.loading,
    changeStream,
    copyTemplateStream,
    getSavedStreams,
    onboardingPassedRequest,
    refetchFeaturesOnboarding,
    pointSetDevice,
  };
};

export default useSelectPlayerModalViewModel;
